"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Repeat, AlertCircle } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { can } from "@/utils/service";
import { money, label, shortDate } from "@/utils/api";
import { useCounts, countText } from "@/components/crm/useCounts";
import ListShell, { toolbarInput, pill } from "@/components/crm/ListShell";
import { useRemoteList } from "@/components/crm/useRemoteList";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";
import type { ContractRecord as Contract } from "@/components/contracts/types";

const STATUS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700", active: "bg-green-100 text-green-800", ended: "bg-gray-100 text-gray-600", cancelled: "bg-red-100 text-red-800",
  renewed: "bg-purple-100 text-purple-800", expired: "bg-orange-100 text-orange-800",
};

export default function ContractsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [expiring, setExpiring] = useState(false);
  const [sort, setSort] = useState("-start_date");
  const list = useRemoteList<Contract>("/contracts", { search: query, status, expiring: expiring ? "1" : undefined, within_days: expiring ? "60" : undefined, sort });
  const { counts, loading: countsLoading } = useCounts("/contracts", { all: {}, active: { status: "active" }, expiring: { expiring: "1", within_days: "60" }, ended: { status: "ended,cancelled" } }, list.rows.length);
  const navigate = useNavigate();

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const card = (c: Contract) => {
    const days = c.end_date ? Math.ceil((new Date(`${c.end_date}T00:00:00`).getTime() - Date.now()) / 86_400_000) : null;
    const live = c.status === "active";
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500">{c.number ?? "Draft contract"}</p>
            <p className="truncate text-sm font-semibold text-gray-900">{c.title ?? label(c.service_type)}</p>
            <p className="truncate text-xs text-gray-500">{c.account?.name ?? "—"}</p>
          </div>
          <span className={pill(STATUS[c.status] ?? STATUS.draft)}>{label(c.status)}</span>
        </div>
        <p className="mt-4 text-lg font-semibold text-gray-900">{c.billing_mode === "variable" ? "Variable amount" : money(c.value, c.currency)} <span className="text-xs font-normal text-gray-500">{c.billing_mode === 'percentage' ? 'total contract value' : `/ ${label(c.billing_cycle)}`}</span></p>
        <p className="mt-1 text-xs text-gray-600">{shortDate(c.start_date)} → {shortDate(c.end_date)}</p>
        <div className="min-h-[1rem] flex-1" />
        {can('invoices.create') && can('invoices.view') && <button type="button" className="my-3 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white" onClick={(event) => { event.stopPropagation(); navigate(`/contracts/${c.id}?generate=1`); }}>Generate invoice from contract</button>}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs">
          {live && days !== null ? (
            <span className={`inline-flex items-center gap-1 ${c.is_expiring_soon ? "font-semibold text-amber-600" : days < 0 ? "text-red-600" : "text-gray-600"}`}>
              {c.is_expiring_soon && <AlertCircle className="h-3.5 w-3.5" />}
              {days < 0 ? `Ended ${-days} day${days === -1 ? "" : "s"} ago` : days === 0 ? "Ends today" : `${days} day${days === 1 ? "" : "s"} left`}
            </span>
          ) : <span className="text-gray-400">{c.renewal_of ? `Renews ${c.renewal_of.number}` : ""}</span>}
          <span className="inline-flex items-center gap-1 text-gray-500">{c.auto_renew && <><Repeat className="h-3.5 w-3.5" />Auto-renews</>}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Contracts" description="Service agreements, renewals and recurring billing" />
        <ListShell
          renderCard={card}
          rowKey={(r) => r.id}
          cardClass={(c) => (c.is_expiring_soon ? "border-amber-300 ring-1 ring-amber-200" : "")}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          noun="contracts"
          onRowClick={(contract) => navigate(`/contracts/${contract.id}`)}
          grid={{
            id: "contracts",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search number, title or account…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (<>
              <label className="text-xs text-gray-600">Status
                <AppSelect className={`${toolbarInput} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {Object.keys(STATUS).map((s) => <option key={s} value={s}>{label(s)}</option>)}
                </AppSelect>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" className="accent-primary" checked={expiring} onChange={(e) => setExpiring(e.target.checked)} />Ending in the next 60 days</label>
            </>),
            activeFilters: (status ? 1 : 0) + (expiring ? 1 : 0),
            onClearFilters: () => { setStatus(""); setExpiring(false); },
            sortOptions: [
              { value: "-start_date", label: "Newest start" }, { value: "end_date", label: "Ending soonest" }, { value: "renewal_date", label: "Renewal date" },
              { value: "-value", label: "Highest value" }, { value: "number", label: "Number" },
            ],
            sort, onSort: setSort,
            stats: [
              { label: "All contracts", value: countText(counts.all) },
              { label: "Active", value: countText(counts.active), tone: "good" },
              { label: "Ending in 60 days", value: countText(counts.expiring), tone: counts.expiring ? "warn" : "default" },
              { label: "Ended or cancelled", value: countText(counts.ended) },
            ],
            statsLoading: countsLoading,
            exportEntity: "contracts", exportPermission: "contracts.export",
          }}
          onAdd={can("contracts.create") ? () => navigate("/contracts/new") : undefined}
          addLabel="New Contract"
          onPerPage={list.setPerPage}
          empty={{ icon: FileSignature, title: "No contracts", text: expiring ? "No active contracts end in the next 60 days." : "Add service agreements to track renewals and bill each period automatically.", action: "Create First Contract" }}
        />
      </div>


    </div>
  );
}
